/**
 * All-in-adjusted EV net result for a completed hand (docs/UI_REFERENCE.md
 * "フィードバック反映" -- history P/L graph, series 2).
 *
 * Definition:
 *  - fold-ended hands: EV == actual net result (nothing to adjust; the
 *    hand never reached a runout).
 *  - showdown hands whose last logged action was on the river: EV ==
 *    actual net result (both players had a decision on every street, so
 *    there was no auto-dealt runout to smooth out variance for).
 *  - showdown hands whose last logged action was pre-river (i.e. someone
 *    was all-in and the remaining board was auto-dealt with no further
 *    action): EV = heroEquityAtThatStreet * finalPot - heroTotalInvested,
 *    with equity estimated via Monte-Carlo (`estimateEquity`) using the
 *    board as it stood right after the last action, both players' actual
 *    hole cards, and a fixed RNG seed for determinism.
 */
import { mulberry32, type Card, type HandRecord, type Street } from "@joker-holdem/core";
import { estimateEquity, JOKER } from "@joker-holdem/bot";

const STREET_ORDER: readonly Street[] = ["preflop", "flop", "turn", "river"];

/** Fixed seed for the equity Monte-Carlo sample so a given hand's EV is reproducible. */
const EV_RNG_SEED = 20260717;
const EV_SAMPLES = 2000;

/** Builds a BotView-style visible-board array ([] preflop, [JOKER, ...reals] from flop) as of the given street. */
function visibleBoardAt(record: HandRecord, street: Street): Card[] {
  if (street === "preflop") return [];
  const board = record.board;
  const cards: Card[] = [JOKER];
  if (board.flopReal) cards.push(board.flopReal[0], board.flopReal[1]);
  if (street !== "flop" && board.turn !== undefined) cards.push(board.turn);
  if (street === "river" && board.river !== undefined) cards.push(board.river);
  return cards;
}

/** The last street on which either player took a logged action, or null if none did (shouldn't happen for a completed hand). */
function lastActionedStreet(record: HandRecord): Street | null {
  let last: Street | null = null;
  for (const street of STREET_ORDER) {
    const data = record.streets[street];
    if (data && data.actions.length > 0) last = street;
  }
  return last;
}

/** Computes hero's (seat 0) all-in-adjusted EV net result for a completed hand, in bb. */
export function computeEvNetBb(record: HandRecord): number {
  const actualNetBb = record.netResult[0] / 2;
  if (record.result.endedBy !== "showdown") return actualNetBb;

  const lastStreet = lastActionedStreet(record);
  if (lastStreet === null || lastStreet === "river") return actualNetBb;

  const heroHole = record.holeCards[0];
  const oppHole = record.holeCards[1];
  const board = visibleBoardAt(record, lastStreet);
  const rng = mulberry32(EV_RNG_SEED);
  const equity = estimateEquity(heroHole, board, {
    rng,
    samples: EV_SAMPLES,
    opponentHole: oppHole,
  });

  const finalPotChips = record.result.potAwarded[0] + record.result.potAwarded[1];
  const heroInvestedChips = record.result.potAwarded[0] - record.netResult[0];
  const evChips = equity * finalPotChips - heroInvestedChips;
  return evChips / 2;
}
